import { Link } from "react-router-dom";
import { PRACTICE_AREAS, HORSE_IMAGE, CORE_PRACTICE_AREAS, CLIENTS_SERVED, FIRM_DISCIPLINES } from "@/lib/content";
import SEO from "@/components/SEO";
import PracticeByline from "@/components/PracticeByline";
import { breadcrumbSchema, continuumServiceSchemas, authoredPracticePageSchema } from "@/lib/schemas";

export default function Practice() {
  return (
    <div data-testid="practice-page" className="bg-ivory">
      <SEO
        title="Services — The Business, Wealth & Legacy Lifecycle"
        description="Business law, bankruptcy restructuring, physician-practice counsel, trust planning, asset protection, and estate planning across Texas — the Lone Star Counsel Business, Wealth & Legacy Lifecycle delivered through Six Strategic Service Pillars."
        path="/practice"
        schema={[
          ...authoredPracticePageSchema({
            path: "/practice",
            title: "Services — The Business, Wealth & Legacy Lifecycle",
            description: "Business law, bankruptcy restructuring, physician-practice counsel, trust planning, asset protection, and estate planning across Texas — the Lone Star Counsel Business, Wealth & Legacy Lifecycle delivered through Six Strategic Service Pillars.",
            discipline: "Business, Wealth & Legacy Lifecycle",
            minutes: 12,
            lastReviewed: "2026-02-01",
            publishedDate: "2024-01-15",
          }),
          ...continuumServiceSchemas(PRACTICE_AREAS),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/practice" },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="relative panel-navy min-h-[60vh] flex items-end" data-testid="practice-hero">
        <div className="absolute inset-0">
          <img src={HORSE_IMAGE} alt="Heritage" className="w-full h-full object-cover opacity-30" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-navy/40 via-navy/40 to-navy"></div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-32 md:py-40 w-full">
          <div className="kicker kicker-ivory">The Lone Star Counsel</div>
          <div className="mt-3 text-[11px] uppercase tracking-[0.42em] text-brass font-medium" data-testid="practice-disciplines">
            {FIRM_DISCIPLINES.split(". ").filter(Boolean).join(" · ").replace(/\.$/, "")}
          </div>
          <h1 className="mt-8 font-display text-ivory text-6xl md:text-8xl leading-[0.95] tracking-tight">
            Business, Wealth &amp; Legacy<br/>
            <span className="italic font-cormorant text-brass">Lifecycle.</span>
          </h1>
          <p className="mt-8 max-w-2xl font-serif italic text-2xl text-ivory/85 leading-snug">
            The distinctive framework by which we serve you — delivered through <span className="text-brass not-italic tracking-wide">Six Strategic Service Pillars.</span> One firm, end to end.
          </p>
          <div className="mt-10">
            <Link to="/intake" className="btn-brass" data-testid="practice-hero-intake">
              Discuss your matter →
            </Link>
          </div>
        </div>
      </section>

      <PracticeByline
        minutes={12}
        lastReviewed="February 2026"
        discipline="Practice Continuum"
      />

      {/* Core Practice Areas — plain-language disciplines */}
      <section className="bg-ivory border-b-0 py-24 md:py-32" data-testid="core-practice-areas">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row items-end justify-between gap-10 mb-16">
            <div className="max-w-3xl">
              <div className="kicker">Core Practice Areas</div>
              <h2 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
                Five disciplines.<br/>
                <span className="italic font-cormorant text-oxblood">One integrated counsel.</span>
              </h2>
            </div>
            <p className="lg:max-w-md text-slate2 font-light text-base leading-relaxed">
              The plain-language listing of what we do — drawn from a single firm that combines attorney,
              CPA, and business-advisor credentials under one roof.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-16">
            {CORE_PRACTICE_AREAS.map((cpa, idx) => (
              <article
                key={cpa.title}
                id={cpa.anchorId || undefined}
                className={`hairline pt-8 ${idx === CORE_PRACTICE_AREAS.length - 1 && CORE_PRACTICE_AREAS.length % 2 === 1 ? "lg:col-span-2 lg:max-w-3xl" : ""}`}
                data-testid={`core-area-${idx}`}
              >
                <div className="flex items-baseline gap-4">
                  <span className="font-cormorant italic text-2xl md:text-3xl text-brass">{cpa.no}</span>
                  <h3 className="font-display text-3xl md:text-4xl text-navy leading-[1.05]">{cpa.title}</h3>
                </div>
                <div className="mt-3 font-cormorant italic text-lg md:text-xl text-oxblood">{cpa.italic}</div>
                <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3" data-testid={`core-area-services-${idx}`}>
                  {cpa.services.map((s, i) => (
                    <li key={i} className="flex items-start gap-3 text-base text-ink/85 font-cormorant leading-snug">
                      <span className="text-brass mt-1.5 text-[0.55rem]">◆</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
                {idx === 1 && (
                  <div className="mt-6 hairline-top pt-5 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-[0.22em]" data-testid="bankruptcy-deep-links">
                    <Link to="/practice/subchapter-v" className="text-oxblood hover:text-navy transition border-b border-oxblood/40 pb-1">Subchapter V →</Link>
                    <Link to="/practice/chapter-7-business" className="text-oxblood hover:text-navy transition border-b border-oxblood/40 pb-1">Chapter 7 — Business →</Link>
                    <Link to="/practice/chapter-7-individual" className="text-oxblood hover:text-navy transition border-b border-oxblood/40 pb-1">Chapter 7 — Individual →</Link>
                  </div>
                )}
                {cpa.title === "Trust Planning & Asset Protection" && (
                  <div className="mt-6 hairline-top pt-5 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-[0.22em]" data-testid="trusts-deep-links">
                    <Link to="/trusts" className="text-oxblood hover:text-navy transition border-b border-oxblood/40 pb-1">Trusts Hub →</Link>
                    <Link to="/trusts/revocable-living-trusts" className="text-oxblood hover:text-navy transition border-b border-oxblood/40 pb-1">Revocable Living →</Link>
                    <Link to="/trusts/irrevocable-trusts" className="text-oxblood hover:text-navy transition border-b border-oxblood/40 pb-1">Irrevocable →</Link>
                    <Link to="/trusts/business-owner-trust-planning" className="text-oxblood hover:text-navy transition border-b border-oxblood/40 pb-1">Business Owner →</Link>
                    <Link to="/trusts/trust-administration" className="text-oxblood hover:text-navy transition border-b border-oxblood/40 pb-1">Administration →</Link>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Services Index — full 72-service roster at a glance */}
      <section className="bg-ivory border-y border-transparent py-20 md:py-28" data-testid="services-index">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row items-end justify-between gap-10 mb-16">
            <div className="max-w-2xl">
              <div className="kicker">How We Deliver The Lifecycle</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.0]">
                Six Strategic<br/>
                <span className="italic font-cormorant text-oxblood">Service Pillars.</span>
              </h2>
            </div>
            <p className="lg:max-w-sm text-slate2 font-light text-base leading-relaxed">
              The Business, Wealth &amp; Legacy Lifecycle is delivered through six strategic pillars —
              seventy-two representative services. Click a pillar below to jump to its full editorial treatment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 lg:divide-x divide-brass/30 border-y border-transparent">
            {PRACTICE_AREAS.map((p) => (
              <a
                key={p.slug}
                href={`#${p.slug}`}
                className="group py-8 xl:py-10 xl:px-6 first:xl:pl-0 last:xl:pr-0 border-b xl:border-b-0 border-transparent last:border-b-0"
                data-testid={`services-index-${p.slug}`}
              >
                <div className="serif-num text-4xl text-oxblood leading-none">{p.no}</div>
                <div className="mt-4 font-display text-2xl text-navy group-hover:text-oxblood transition-colors">
                  {p.title}
                </div>
                <div className="font-cormorant italic text-base text-brass mt-1">{p.serif}</div>
                <ul className="mt-6 space-y-2 text-sm font-cormorant italic text-slate2/90 leading-snug">
                  {p.services.map((s, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-brass mt-1 text-[0.55rem]">◆</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Detail list */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="kicker mb-4">In Depth</div>
        <h2 className="font-display text-4xl md:text-6xl leading-[1.0] max-w-3xl mb-16">
          Each chapter,<br/>
          <span className="italic font-cormorant text-oxblood">in full.</span>
        </h2>
        {PRACTICE_AREAS.map((p, idx) => (
          <article
            key={p.slug}
            id={p.slug}
            data-testid={`practice-area-${p.slug}`}
            className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 py-16 md:py-24 border-b-0 last:border-b-0"
          >
            <div className="lg:col-span-4">
              <img
                src={`/img/practice-${p.slug}.jpg`}
                alt=""
                loading="lazy"
                className="w-full aspect-[4/3] object-cover mb-8"
                style={{ filter: "sepia(0.18) contrast(1.02)" }}
                data-testid={`practice-signature-${p.slug}`}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <div className="serif-num text-7xl md:text-8xl text-oxblood leading-none">{p.no}</div>
              <div className="kicker mt-6">{p.title}</div>
              <h2 className="mt-3 font-display text-5xl md:text-6xl italic font-cormorant text-navy leading-[1.0]">
                {p.serif}
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <p className="font-display text-2xl md:text-3xl text-ink leading-snug font-light">
                {p.blurb}
              </p>
              <p className="mt-8 text-lg text-slate2 font-light leading-relaxed">
                {p.detail}
              </p>

              {/* Services roster */}
              <div className="mt-12 hairline pt-8">
                <div className="kicker mb-6">Representative Services</div>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3" data-testid={`practice-services-${p.slug}`}>
                  {p.services.map((s, i) => (
                    <li key={i} className="flex items-start gap-3 text-base md:text-lg font-cormorant text-ink/85 leading-snug">
                      <span className="text-brass mt-1.5 text-xs">◆</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-6">
                <Link to="/intake" className="btn-primary" data-testid={`practice-cta-${p.slug}`}>
                  Discuss this practice →
                </Link>
                <span className="font-cormorant italic text-slate2">
                  or <Link to="/contact" className="link-underline text-oxblood">write to the firm</Link>
                </span>
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* Private Client Services — bespoke, concierge-level counsel for HNW families */}
      <section className="bg-linen py-24 md:py-32" data-testid="private-client-services">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="kicker">Private Client Services</div>
              <h2 className="mt-6 font-display text-5xl md:text-6xl leading-[0.98]">
                For the <span className="italic font-cormorant text-oxblood">quietly consequential.</span>
              </h2>
              <p className="mt-8 font-cormorant italic text-xl md:text-2xl text-slate2 leading-snug max-w-md">
                A private tier of engagement for families and principals whose affairs require
                singular discretion, coordination, and continuity.
              </p>
              <p className="mt-6 text-base md:text-lg text-slate2 font-light leading-relaxed max-w-md">
                Retained on annual engagement, our Private Client relationships extend beyond
                discrete matters. We serve as the standing legal seat at the family table —
                coordinating with your accountants, wealth advisors, trustees, and family office
                to ensure every decision is deliberate, documented, and durable.
              </p>
              <div className="mt-10">
                <Link to="/practice/private-client" className="btn-primary" data-testid="private-client-cta">
                  Learn About Private Client →
                </Link>
              </div>
            </div>

            <ul className="lg:col-span-6 lg:col-start-7 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10" data-testid="private-client-list">
              {[
                {
                  no: "I",
                  title: "Family Office Coordination",
                  body: "Standing counsel to single-family offices — governance documents, investment entity structuring, and coordination with in-house CFOs and external fiduciaries.",
                },
                {
                  no: "II",
                  title: "Multi-Generational Wealth",
                  body: "Dynasty trusts, GST-exempt planning, and generation-skipping structures designed to endure across three generations without reformation.",
                },
                {
                  no: "III",
                  title: "Concierge Legal Counsel",
                  body: "Direct-line access to counsel for matters personal, familial, and enterprise — from a discreet real-estate closing to a delicate family conversation.",
                },
                {
                  no: "IV",
                  title: "Fiduciary Oversight",
                  body: "Independent review of trustees, executors, and investment advisors. We audit fiduciary conduct, verify accountings, and — when necessary — remove and replace.",
                },
                {
                  no: "V",
                  title: "Legacy Governance",
                  body: "Family constitutions, heir councils, and succession charters that transmit not merely assets, but the judgment and values that produced them.",
                },
                {
                  no: "VI",
                  title: "Cross-Border Coordination",
                  body: "For families holding interests in India, the U.K., or the E.U. — coordinated planning across jurisdictions, with local counsel of record engaged as needed.",
                },
              ].map((item, i) => (
                <li key={i} className="hairline-top pt-6" data-testid={`private-client-item-${i}`}>
                  <div className="flex items-baseline gap-4">
                    <span className="font-cormorant italic text-2xl text-brass">{item.no}</span>
                    <h3 className="font-display text-2xl md:text-[1.65rem] text-navy leading-[1.15]">
                      {item.title}
                    </h3>
                  </div>
                  <p className="mt-3 text-base text-slate2 font-light leading-relaxed">
                    {item.body}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-16 hairline-top pt-10">
            <p className="font-cormorant italic text-lg md:text-xl text-slate2 max-w-3xl leading-snug">
              Private Client engagement is offered by referral and mutual acceptance. New relationships
              are limited each year to preserve the standard of service. Terms and annual retainer are
              furnished on request.
            </p>
          </div>
        </div>
      </section>

      {/* Clients Served */}
      <section className="panel-navy py-24 md:py-32" data-testid="clients-served">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="kicker kicker-ivory">Clients Served</div>
              <h2 className="mt-6 font-display text-5xl md:text-6xl text-ivory leading-[0.98]">
                Who we<br/>
                <span className="italic font-cormorant text-brass">advise.</span>
              </h2>
              <p className="mt-8 font-cormorant italic text-xl md:text-2xl text-ivory/85 leading-snug max-w-md">
                A deliberate clientele — Texas entrepreneurs, professionals, and families
                whose matters require both legal precision and accounting fluency.
              </p>
            </div>

            <ul className="lg:col-span-6 lg:col-start-7 space-y-5" data-testid="clients-served-list">
              {CLIENTS_SERVED.map((c, i) => (
                <li
                  key={i}
                  className="hairline-top pt-5 flex items-baseline gap-5"
                  data-testid={`client-served-${i}`}
                >
                  <span className="font-cormorant italic text-2xl text-brass w-12 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-display text-xl md:text-2xl text-ivory leading-snug">{c}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-16 hairline-top pt-10 flex flex-wrap items-center justify-between gap-6">
            <p className="font-cormorant italic text-xl md:text-2xl text-brass max-w-xl leading-snug">
              &ldquo;If the matter is private, particular, and patient — we are likely the right firm.&rdquo;
            </p>
            <Link to="/intake" className="btn-brass" data-testid="clients-served-cta">
              Request a Conference →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
